import {
  AnimatePresence,
  motion,
} from "framer-motion";

import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import siteConfig from "../../../site/config/site";

import {
  getRoutePath,
} from "../../../site/config/routes";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  CINEMATIC HERO
  ============================================================

  Hero tukee teemakohtaista mediaa.

  Esimerkiksi:

  Ivory:
  - valoisa video
  - tumma typografia
  - kevyt fine-art blur

  Noir:
  - tumma cinematic-video
  - vaalea typografia
  - terävämpi media

  Hero-media tulee kokonaan siteConfigista.
*/

export default function Hero({
  language = "fi",
  theme = "ivory",
}) {
  const {
    t,
  } = useTranslation("common");

  const heroConfig =
    siteConfig.hero || {};

  /*
    Haetaan nykyiselle teemalle määritetty media.

    Jos teemaa ei löydy, kokeillaan Ivorya ja sen jälkeen Noiria.
  */
  const activeMedia =
    heroConfig.media?.[theme] ||
    heroConfig.media?.ivory ||
    heroConfig.media?.noir ||
    {};

  const mediaType =
    activeMedia.type ||
    "image";

  const mediaSource =
    activeMedia.src ||
    "";

  const hasImage =
    mediaType === "image" &&
    Boolean(mediaSource);

  const hasVideo =
    mediaType === "video" &&
    Boolean(mediaSource);

  const focalPoint =
    activeMedia.focalPoint ||
    "50% 50%";

  const poster =
    activeMedia.poster ||
    "";

 /*
   Teemakohtainen pystysiirto.

   Desktop ja mobiili voidaan rajata erikseen.
   Positiivinen arvo siirtää mediaa alaspäin.
 */
 const mediaOffsetY =
   Number(
     activeMedia.offsetY ||
     0
   );

 const mobileMediaOffsetY =
   Number(
     activeMedia.mobileOffsetY ||
     0
   );

 /*
   Mediaa kasvatetaan pystysuunnassa offsetin verran,
   jotta siirtäminen ei jätä tyhjää raitaa heron yläreunaan.
 */
 const mediaOverscanY =
   Math.abs(
     mediaOffsetY
   );

 const mobileMediaOverscanY =
   Math.abs(
     mobileMediaOffsetY
   );

  /*
    Teemakohtainen blur.

    Rajataan arvo turvallisesti välille 0–10 px,
    jotta vahingossa annettu väärä config-arvo ei hajota näkymää.
  */
  const mediaBlur =
    Math.min(
      10,
      Math.max(
        0,
        Number(
          activeMedia.blur ||
          0
        )
      )
    );

  /*
    Blur voi tuoda elementin reunoja näkyviin.

    Siksi media voidaan suurentaa aivan hieman.
  */
  const mediaScale =
    Math.max(
      1,
      Number(
        activeMedia.mediaScale ||
        1
      )
    );

  /*
    Jos overlay on joskus suoraan numero,
    tuetaan myös sitä.

    Normaalisti käytämme tällä hetkellä "theme"-arvoa,
    jolloin CSS määrittää Ivory / Noir -käsittelyn.
  */
  const customOverlayOpacity =
    typeof heroConfig.overlay === "number"
      ? Math.min(
          1,
          Math.max(
            0,
            heroConfig.overlay
          )
        )
      : null;

  return (
    <section
      className="
        hero-premium
        relative
        isolate
        flex
        min-h-[100svh]
        w-full
        overflow-hidden
      "
      aria-label={t(
        "hero.eyebrow"
      )}
    >
      {/* ====================================================
          THEME-SPECIFIC MEDIA
          ==================================================== */}

      <div
        className={`
          absolute
          bottom-0
          left-0
          right-0
          -z-30
          overflow-hidden

          ${
            theme === "ivory"
              ? "top-0 lg:top-[88px]"
              : "top-0"
          }
        `}
        aria-hidden="true"
      >
        <AnimatePresence
          mode="sync"
          initial={false}
        >
          <motion.div
            key={`${theme}-${mediaSource}`}
            className="
              absolute
              inset-0
              overflow-hidden
            "
            initial={{
              opacity: 0,
              scale: 1.015,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              scale: 1.005,
            }}
            transition={{
              opacity: {
                duration: 0.7,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              },

              scale: {
                duration: 1.4,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              },
            }}
          >
            {hasVideo ? (
            <video
              className="
                absolute
                left-0
                w-full
                object-cover

                [top:var(--hero-media-mobile-top)]
                [height:calc(100%+var(--hero-media-mobile-extra))]
                [transform:translateY(var(--hero-media-mobile-offset))_scale(var(--hero-media-scale))]

                lg:[top:var(--hero-media-desktop-top)]
                lg:[height:calc(100%+var(--hero-media-desktop-extra))]
                lg:[transform:translateY(var(--hero-media-desktop-offset))_scale(var(--hero-media-scale))]
              "
              style={{
                objectPosition:
                  focalPoint,

                "--hero-media-mobile-top":
                  `${-mobileMediaOverscanY}px`,

                "--hero-media-mobile-extra":
                  `${mobileMediaOverscanY * 2}px`,

                "--hero-media-mobile-offset":
                  `${mobileMediaOffsetY}px`,

                "--hero-media-desktop-top":
                  `${-mediaOverscanY}px`,

                "--hero-media-desktop-extra":
                  `${mediaOverscanY * 2}px`,

                "--hero-media-desktop-offset":
                  `${mediaOffsetY}px`,

                "--hero-media-scale":
                  mediaScale,

                filter:
                  mediaBlur > 0
                    ? `blur(${mediaBlur}px)`
                    : "none",
              }}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={
                poster ||
                undefined
              }
            >
              <source
                src={mediaSource}
                type="video/mp4"
              />
            </video>
            ) : hasImage ? (
            <img
              src={mediaSource}
              alt=""
              className="
                absolute
                left-0
                w-full
                object-cover

                [top:var(--hero-media-mobile-top)]
                [height:calc(100%+var(--hero-media-mobile-extra))]
                [transform:translateY(var(--hero-media-mobile-offset))_scale(var(--hero-media-scale))]

                lg:[top:var(--hero-media-desktop-top)]
                lg:[height:calc(100%+var(--hero-media-desktop-extra))]
                lg:[transform:translateY(var(--hero-media-desktop-offset))_scale(var(--hero-media-scale))]
              "
              style={{
                objectPosition:
                  focalPoint,

                "--hero-media-mobile-top":
                  `${-mobileMediaOverscanY}px`,

                "--hero-media-mobile-extra":
                  `${mobileMediaOverscanY * 2}px`,

                "--hero-media-mobile-offset":
                  `${mobileMediaOffsetY}px`,

                "--hero-media-desktop-top":
                  `${-mediaOverscanY}px`,

                "--hero-media-desktop-extra":
                  `${mediaOverscanY * 2}px`,

                "--hero-media-desktop-offset":
                  `${mediaOffsetY}px`,

                "--hero-media-scale":
                  mediaScale,

                filter:
                  mediaBlur > 0
                    ? `blur(${mediaBlur}px)`
                    : "none",
              }}
            />
            ) : (
              /*
                Kehitysvaiheen fallback.
              */
              <div
                className="
                  hero-premium__fallback
                  h-full
                  w-full
                "
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ====================================================
          THEME-AWARE OVERLAY
          ==================================================== */}

      <div
        className="
          hero-premium__overlay
          absolute
          inset-0
          -z-20
        "
        style={
          customOverlayOpacity !==
          null
            ? {
                opacity:
                  customOverlayOpacity,
              }
            : undefined
        }
        aria-hidden="true"
      />

      {/*
        Teemakohtainen scrim auttaa tekstin luettavuutta.
      */}

      <div
        className="
          hero-premium__scrim
          absolute
          inset-0
          -z-10
        "
        aria-hidden="true"
      />

      {/* ====================================================
          CONTENT
          ==================================================== */}

      <div
        className="
          mx-auto
          flex
          min-h-[100svh]
          w-full
          max-w-editorial
          flex-col
          justify-end
          px-6
          pb-10
          pt-32
          sm:px-10
          sm:pb-14
          lg:px-16
          lg:pb-16
          lg:pt-40
        "
      >
        <div
          className="
            grid
            items-end
            gap-12
            lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.38fr)]
            lg:gap-16
          "
        >
          {/* =================================================
              PRIMARY
              ================================================= */}

          <div className="max-w-5xl">
            <motion.p
              className="
                hero-premium__muted
                mb-6
                text-[10px]
                font-medium
                uppercase
                tracking-[0.34em]
                sm:text-xs
              "
              initial={{
                opacity: 0,
                y: 14,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.15,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              {t(
                "hero.eyebrow"
              )}
            </motion.p>

            <motion.h1
              className="
                max-w-[1100px]
                font-display
                text-[clamp(3.5rem,12vw,9.5rem)]
                font-normal
                leading-[0.83]
                tracking-[-0.055em]
              "
              initial={{
                opacity: 0,
                y: 32,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 1.05,
                delay: 0.22,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              {t(
                "hero.title"
              )}
            </motion.h1>
          </div>

          {/* =================================================
              SECONDARY
              ================================================= */}

          <motion.div
            className="
              max-w-md
              lg:pb-2
            "
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.9,
              delay: 0.42,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <p
              className="
                hero-premium__muted
                max-w-sm
                text-sm
                leading-7
                sm:text-base
              "
            >
              {t(
                "hero.description"
              )}
            </p>

            <div
              className="
                mt-8
                flex
                flex-col
                items-start
                gap-3
                sm:flex-row
                sm:flex-wrap
                lg:flex-col
              "
            >
              <Link
                to={getRoutePath(
                  "portfolio",
                  language
                )}
                className="
                  hero-premium__primary
                  inline-flex
                  min-h-12
                  items-center
                  justify-center
                  border
                  px-6
                  py-3
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                "
              >
                {t(
                  "hero.portfolio"
                )}
              </Link>

              <Link
                to={getRoutePath(
                  "services",
                  language
                )}
                className="
                  hero-premium__secondary
                  group
                  inline-flex
                  min-h-12
                  items-center
                  gap-3
                  border
                  px-6
                  py-3
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  backdrop-blur-sm
                "
              >
                <span>
                  {t(
                    "hero.services"
                  )}
                </span>

                <span
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* ====================================================
            BOTTOM
            ==================================================== */}

        <motion.div
          className="
            hero-premium__line
            mt-12
            flex
            items-end
            justify-between
            border-t
            pt-5
            sm:mt-16
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          transition={{
            duration: 1,
            delay: 0.7,
          }}
        >
          <p
            className="
              hero-premium__muted
              m-0
              text-[9px]
              uppercase
              tracking-[0.22em]
              sm:text-[10px]
            "
          >
            {
              siteConfig.brand.location
            }
          </p>

          {heroConfig.showScrollCue !==
            false && (
            <div
              className="
                hero-premium__muted
                flex
                items-center
                gap-3
                text-[9px]
                uppercase
                tracking-[0.2em]
                sm:text-[10px]
              "
            >
              <span
                className="
                  hidden
                  sm:inline
                "
              >
                {t(
                  "hero.scroll"
                )}
              </span>

              <motion.span
                className="
                  hero-premium__scroll
                  flex
                  h-9
                  w-6
                  items-start
                  justify-center
                  rounded-full
                  border
                  pt-2
                "
                aria-hidden="true"
              >
                <motion.span
                  className="
                    hero-premium__scroll-dot
                    block
                    h-1
                    w-1
                    rounded-full
                  "
                  animate={{
                    y: [
                      0,
                      12,
                      0,
                    ],

                    opacity: [
                      0.4,
                      1,
                      0.4,
                    ],
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.span>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

